import React, { useEffect, useMemo, useState } from "react";
import { AxiosInstance } from "axios";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericCheckbox from "@ui-components/Checkbox/GenericCheckbox";
import GenericInlineAlert from "@ui-components/Alert/GenericInlineAlert";
import {
  CarrierFormErrors,
  CarrierFormState,
  buildCarrierPayload,
  describeCarrierError,
  emptyCarrierForm,
  formFromCarrier,
  validateCarrierForm,
} from "./carrierForm";
import { carriersAdminText } from "./carriersLabels";
import { CarrierResponse, createCarrier, updateCarrier } from "./request/request";

interface CarrierModalProps {
  isOpen: boolean;
  /** The carrier to edit; null creates one. */
  carrier: CarrierResponse | null;
  api: AxiosInstance;
  onClose: () => void;
  /** Called after a successful save so the list can refresh. */
  onSaved: () => void;
}

const text = carriersAdminText.modal;

/**
 * Create or edit one carrier (CTX-786), as the approved design: name, an
 * optional code and whether new Carrier Bridge services can pick it. A
 * duplicate name or code is shown on the form and marks its field.
 */
const CarrierModal: React.FC<CarrierModalProps> = ({
  isOpen,
  carrier,
  api,
  onClose,
  onSaved,
}) => {
  const initialForm = useMemo(
    () => (carrier ? formFromCarrier(carrier) : emptyCarrierForm),
    [carrier]
  );
  const [form, setForm] = useState<CarrierFormState>(initialForm);
  const [errors, setErrors] = useState<CarrierFormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(initialForm);
    setErrors({});
    setApiError(null);
  }, [isOpen, initialForm]);

  const update = <K extends keyof CarrierFormState>(
    key: K,
    value: CarrierFormState[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSave = async () => {
    const found = validateCarrierForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setIsSaving(true);
    setApiError(null);
    try {
      if (!carrier) {
        await createCarrier(api, buildCarrierPayload(form));
      } else {
        const changes = buildCarrierPayload(form, initialForm);
        if (Object.keys(changes).length > 0) {
          await updateCarrier(api, carrier.id, changes);
        }
      }
      onSaved();
      onClose();
    } catch (error) {
      // Stays open with the reason instead of closing on a failed save.
      console.error("Error saving carrier", error);
      const described = describeCarrierError(error);
      setApiError(described.message);
      // A blank error marks the field red; the message is in the alert above.
      if (described.field) {
        setErrors((prev) => ({ ...prev, [described.field as keyof CarrierFormState]: " " }));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <GenericButton
        label={text.cancel}
        variant="secondary"
        onClick={onClose}
        disabled={isSaving}
      />
      <GenericButton
        label={text.save}
        variant="brand"
        onClick={handleSave}
        loading={isSaving}
        disabled={isSaving}
      />
    </div>
  );

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={carrier ? text.editTitle : text.createTitle}
      size="md"
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        {apiError && (
          <GenericInlineAlert variant="error">{apiError}</GenericInlineAlert>
        )}
        <GenericInput
          label={text.name}
          value={form.name}
          onChange={(value) => update("name", value)}
          required
          hasError={Boolean(errors.name)}
          helperText={errors.name?.trim() || undefined}
          fullWidth
        />
        <GenericInput
          label={text.code}
          value={form.code}
          onChange={(value) => update("code", value)}
          hasError={Boolean(errors.code)}
          helperText={errors.code?.trim() || undefined}
          fullWidth
        />
        <div className="flex flex-col gap-1">
          <GenericCheckbox
            label={text.available}
            checked={form.available}
            onChange={(checked) => update("available", checked)}
          />
          <p className="ml-6 text-xs text-semantic-text-secondary">
            {text.availableHint}
          </p>
        </div>
      </div>
    </GenericModal>
  );
};

export default CarrierModal;
